import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  TextField,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import api from "../../../api/client";
import { useTranslation } from "react-i18next";
import { normalizeBankQuestionType, saveAiQuestionToBank } from "../pages/questionBank/questionBankUtils";

const toJdQuestion = (question, timeLimit, questionBankId) => ({
  nameOfQuestion: question.nameOfQuestion,
  type: normalizeBankQuestionType(question.type),
  choices: question.choices,
  createdAt: question.createdAt,
  difficulty: question.difficulty,
  skill_tag: question.skill_tag,
  ...(questionBankId && { questionBankId }),
  ...(normalizeBankQuestionType(question.type) === "coding" && { time_limit: timeLimit }),
});

export default function AIQuestionGeneratorModal({
  open,
  onClose,
  onAddQuestions,
  jobTitle,
  experienceLevel,
  requiredSkills,
  niceToHaveSkills,
  existingQuestions,
  companyId,
}) {
  const { t, i18n } = useTranslation();
  const [aiCategory, setAiCategory] = useState("conceptual");
  const [generatedAiQuestions, setGeneratedAiQuestions] = useState([]);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiTimeLimit, setAiTimeLimit] = useState(5);
  const [isSavingSelected, setIsSavingSelected] = useState(false);
  const [bankSaveError, setBankSaveError] = useState("");

  const handleClose = () => {
    if (isSavingSelected) return;
    setGeneratedAiQuestions([]);
    setBankSaveError("");
    onClose();
  };

  const aiCategoryOptions = [
    { value: "conceptual", label: t('jobDescription.aiQuestionGenerator.categories.conceptual', 'Conceptual'), type: "concept" },
    { value: "coding", label: t('jobDescription.aiQuestionGenerator.categories.coding', 'Coding'), type: "coding" },
    { value: "mcq", label: t('jobDescription.aiQuestionGenerator.categories.mcq', 'Multiple Choice'), type: "mcq" },
    { value: "behavioral", label: t('jobDescription.aiQuestionGenerator.categories.behavioral', 'Behavioral'), type: "behavioral" },
    { value: "general", label: t('jobDescription.aiQuestionGenerator.categories.general', 'General'), type: "general" },
  ];

  const handleGenerateAiQuestions = async (isLoadMore = false) => {
    setIsAiGenerating(true);
    setBankSaveError("");

    const allSkills = [
      ...(requiredSkills || []),
      ...(niceToHaveSkills || [])
    ];

    const existingQuestionsTexts = [
      ...(existingQuestions || []).map(q => typeof q === "object" ? q.nameOfQuestion : q),
      ...generatedAiQuestions.map(q => q.nameOfQuestion)
    ];

    const payload = {
      category: aiCategory,
      skills: allSkills,
      job_title: jobTitle,
      experience_level: experienceLevel,
      existing_questions: existingQuestionsTexts,
      language_code: i18n.language || 'en',
    };

    try {
      const response = await api.post(
        "job-descriptions/generate-interview-questions/",
        payload,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data && response.data.questions) {
        const newQuestions = response.data.questions.map(qObj => ({
          ...qObj,
          nameOfQuestion: qObj.question,
          type: normalizeBankQuestionType(qObj.question_type),
          selectedForJd: false,
          addToQuestionBank: false,
          choices: qObj.choices || [],
          createdAt: new Date().toISOString()
        }));

        setGeneratedAiQuestions(prev => isLoadMore ? [...prev, ...newQuestions] : newQuestions);
      }
    } catch (error) {
      console.error("Error generating AI questions:", error);
    } finally {
      setIsAiGenerating(false);
    }
  };

  const toggleSelectedForJd = (index) => {
    if (isSavingSelected) return;
    setBankSaveError("");
    setGeneratedAiQuestions((prev) => prev.map((q, i) => {
      if (i !== index) return q;
      const selectedForJd = !q.selectedForJd;
      return {
        ...q,
        selectedForJd,
        addToQuestionBank: selectedForJd ? q.addToQuestionBank : false,
      };
    }));
  };

  const toggleAddToQuestionBank = (index) => {
    if (isSavingSelected) return;
    setBankSaveError("");
    setGeneratedAiQuestions((prev) => prev.map((q, i) => (
      i === index && q.selectedForJd ? { ...q, addToQuestionBank: !q.addToQuestionBank } : q
    )));
  };

  const handleAddSelectedAiQuestions = async () => {
    const selectedQuestions = generatedAiQuestions.filter((q) => q.selectedForJd);
    if (selectedQuestions.length === 0 || isSavingSelected) return;

    setIsSavingSelected(true);
    setBankSaveError("");

    try {
      const jdQuestions = [];
      for (const question of selectedQuestions) {
        let questionBankId = null;
        if (question.addToQuestionBank) {
          const saved = await saveAiQuestionToBank(api, question, {
            companyId,
            timeLimit: aiTimeLimit,
          });
          questionBankId = saved?.id || null;
        }
        jdQuestions.push(toJdQuestion(question, aiTimeLimit, questionBankId));
      }

      onAddQuestions(jdQuestions);
      setGeneratedAiQuestions([]);
      onClose();
    } catch (error) {
      console.error("Failed to add selected AI questions to Question Bank:", error);
      setBankSaveError(
        t('jobDescription.aiQuestionGenerator.failedToAddToBank', 'Could not add the selected questions to the Question Bank.')
      );
    } finally {
      setIsSavingSelected(false);
    }
  };

  const selectedCount = generatedAiQuestions.filter(q => q.selectedForJd).length;
  const selectedForBankCount = generatedAiQuestions.filter(q => q.selectedForJd && q.addToQuestionBank).length;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="ai-generator-title"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "700px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: 24,
        }}
      >
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)",
            color: "white",
            position: "relative",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "white !important" }}>
            {t('jobDescription.aiQuestionGenerator.title', 'AI Question Generator')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5, color: "white !important" }}>
            {t('jobDescription.aiQuestionGenerator.subtitle', 'Select questions to add to this job description. Optionally save them to the Question Bank.')}
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isSavingSelected}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "white",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 4, flex: 1, overflowY: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: aiCategory === "coding" ? "1fr 1fr 1fr" : "1fr 1fr" }, gap: 2, mb: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="ai-category-label">{t('jobDescription.aiQuestionGenerator.selectCategory', 'Select Category')}</InputLabel>
              <Select
                labelId="ai-category-label"
                value={aiCategory}
                onChange={(e) => setAiCategory(e.target.value)}
                label={t('jobDescription.aiQuestionGenerator.selectCategory', 'Select Category')}
                renderValue={(selected) => {
                  const selectedOption = aiCategoryOptions.find(opt => opt.value === selected);
                  return selectedOption ? selectedOption.label : selected;
                }}
                MenuProps={{
                  sx: { zIndex: (theme) => theme.zIndex.modal + 3 },
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                      borderRadius: '12px',
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                  }
                }}
              >
                {aiCategoryOptions
                  .filter(opt => !aiCategory || opt.value !== aiCategory)
                  .map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {aiCategory === "coding" && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t('jobDescription.aiQuestionGenerator.timeLimit', 'Time Limit (minutes)')}
                value={aiTimeLimit}
                onChange={(e) => setAiTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                InputProps={{ inputProps: { min: 1, max: 60 } }}
                helperText="Max: 60 min"
              />
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={() => handleGenerateAiQuestions(false)}
              disabled={isAiGenerating || isSavingSelected}
              startIcon={isAiGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeOutlinedIcon />}
              sx={{
                background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0284c7 0%, #c026d3 100%)",
                },
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              {isAiGenerating ? t('jobDescription.aiQuestionGenerator.generating', 'Generating...') : t('jobDescription.aiQuestionGenerator.generateQuestions', 'Generate Questions')}
            </Button>
          </Box>

          {isAiGenerating && generatedAiQuestions.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {generatedAiQuestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {t('jobDescription.aiQuestionGenerator.generatedQuestions', 'Generated Questions')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "primary.main", px: 1.5, py: 0.5, borderRadius: "12px", backgroundColor: "primary.50" }}>
                  {selectedCount} {t('jobDescription.aiQuestionGenerator.selected', 'selected')}
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 2 }}>
                {t('jobDescription.aiQuestionGenerator.selectHint', 'Select a question to add it to this JD. An extra option then appears if you also want it in the Question Bank.')}
              </Typography>

              {bankSaveError && (
                <Typography variant="caption" sx={{ display: "block", color: "#dc2626", mb: 2 }}>
                  {bankSaveError}
                </Typography>
              )}

              <Box sx={{ mb: 3 }}>
                {generatedAiQuestions.map((q, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2.5,
                      mb: 2.5,
                      borderRadius: "12px",
                      border: "1px solid",
                      borderLeft: "4px solid",
                      borderColor: q.selectedForJd ? "primary.main" : "grey.200",
                      borderLeftColor: q.selectedForJd ? "primary.main" : "#60a5fa",
                      backgroundColor: q.selectedForJd ? "primary.50" : "#f9fafb",
                      transition: "all 0.2s ease-in-out",
                      cursor: isSavingSelected ? "default" : "pointer",
                      "&:hover": {
                        borderColor: "primary.light",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      },
                    }}
                    onClick={() => toggleSelectedForJd(idx)}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Checkbox
                        size="small"
                        checked={Boolean(q.selectedForJd)}
                        disabled={isSavingSelected}
                        onChange={() => toggleSelectedForJd(idx)}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          p: 0,
                          mt: 0.25,
                          color: "grey.400",
                          "&.Mui-checked": { color: "#0ea5e9" },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.800", lineHeight: 1.6 }}>
                          {q.nameOfQuestion || q.text}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontWeight: 600, color: q.selectedForJd ? "#0369a1" : "grey.500" }}>
                          {t('jobDescription.aiQuestionGenerator.addToJd', 'Add to JD')}
                        </Typography>
                        {q.type === "mcq" && Array.isArray(q.choices) && (
                          <Box sx={{ mt: 1.5, ml: 0.5, display: "grid", gap: 1.5 }}>
                            {q.choices.map((choice, cIdx) => (
                              <Box key={cIdx} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography variant="caption" sx={{ color: "grey.500", fontWeight: 700, minWidth: 20 }}>
                                  {String.fromCharCode(65 + cIdx)}.
                                </Typography>
                                <Typography variant="body2" sx={{ color: "grey.700", flex: 1, fontSize: "0.875rem" }}>
                                  {choice.text}
                                </Typography>
                                {choice.is_correct && (
                                  <Box sx={{ px: 2, py: 0.5, borderRadius: "100px", backgroundColor: "#dcfce7", color: "#15803d", fontSize: "0.75rem", fontWeight: 600 }}>
                                    {t('jobDescription.correct', 'Correct')}
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                        {q.selectedForJd && (
                          <Box
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                              mt: 1.75,
                              p: 1.5,
                              borderRadius: "10px",
                              backgroundColor: "#fff",
                              border: "1px solid",
                              borderColor: q.addToQuestionBank ? "#7dd3fc" : "#e2e8f0",
                            }}
                          >
                            <FormControlLabel
                              sx={{ m: 0, alignItems: "flex-start" }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={Boolean(q.addToQuestionBank)}
                                  disabled={isSavingSelected}
                                  onChange={() => toggleAddToQuestionBank(idx)}
                                  sx={{
                                    p: 0,
                                    mr: 1,
                                    mt: 0.1,
                                    color: "grey.400",
                                    "&.Mui-checked": { color: "#0ea5e9" },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: q.addToQuestionBank ? "#0369a1" : "grey.700" }}>
                                    {t('jobDescription.aiQuestionGenerator.addToQuestionBank', 'Add to Question Bank')}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "grey.500" }}>
                                    {t('jobDescription.aiQuestionGenerator.addToQuestionBankHint', 'Optional. Keep this question reusable for other job descriptions.')}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleGenerateAiQuestions(true)}
                disabled={isAiGenerating || isSavingSelected}
                sx={{
                  borderColor: "#0ea5e9",
                  color: "#0ea5e9",
                  "&:hover": { borderColor: "#0284c7", backgroundColor: "#f0f9ff" },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "12px",
                }}
              >
                {isAiGenerating ? t('jobDescription.aiQuestionGenerator.loadingMore', 'Loading more...') : t('jobDescription.aiQuestionGenerator.loadMore', 'Load More')}
              </Button>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 3,
            borderTop: "1px solid",
            borderColor: "grey.200",
            display: "flex",
            gap: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={isSavingSelected}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              borderColor: "grey.300",
              color: "grey.700",
              "&:hover": { borderColor: "grey.400", backgroundColor: "grey.100" },
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleAddSelectedAiQuestions}
            disabled={selectedCount === 0 || isSavingSelected}
            startIcon={isSavingSelected ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
              "&:hover": { background: "linear-gradient(135deg, #0284c7 0%, #4f46e5 100%)" },
              "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
            }}
          >
            {isSavingSelected
              ? (selectedForBankCount > 0
                ? t('jobDescription.aiQuestionGenerator.addingToBank', 'Adding to Question Bank...')
                : t('jobDescription.aiQuestionGenerator.addingToJd', 'Adding to JD...'))
              : t('jobDescription.aiQuestionGenerator.addSelected', 'Add Selected to JD')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
